import { Component, Input, OnInit } from '@angular/core';

@Component({
  selector: 'app-circular-progress',
  template: `
    <div class="progress-container" [style.width.px]="size" [style.height.px]="size">
      <svg [attr.width]="size" [attr.height]="size" [attr.viewBox]="'0 0 ' + size + ' ' + size">
        <!-- Background circle -->
        <circle
          [attr.cx]="size/2"
          [attr.cy]="size/2"
          [attr.r]="radius"
          fill="none"
          [attr.stroke]="bgColor"
          [attr.stroke-width]="strokeWidth">
        </circle>

        <!-- Primary Progress circle (up to 100%) -->
        <circle
          [attr.cx]="size/2"
          [attr.cy]="size/2"
          [attr.r]="radius"
          fill="none"
          [attr.stroke]="color"
          [attr.stroke-width]="strokeWidth"
          stroke-linecap="round"
          [attr.stroke-dasharray]="circumference"
          [attr.stroke-dashoffset]="primaryDashOffset"
          [attr.transform]="'rotate(-90 ' + (size/2) + ' ' + (size/2) + ')'">
        </circle>

        <!-- Secondary Progress circle (for progress > 100%) -->
        <circle
          *ngIf="progress > 100"
          [attr.cx]="size/2"
          [attr.cy]="size/2"
          [attr.r]="radius"
          fill="none"
          [attr.stroke]="secondaryColor"
          [attr.stroke-width]="strokeWidth"
          stroke-linecap="round"
          [attr.stroke-dasharray]="circumference"
          [attr.stroke-dashoffset]="secondaryDashOffset"
          [attr.transform]="'rotate(-90 ' + (size/2) + ' ' + (size/2) + ')'">
        </circle>

        <!-- Center content -->
        <foreignObject
          [attr.x]="(size - contentSize) / 2"
          [attr.y]="(size - contentSize) / 2"
          [attr.width]="contentSize"
          [attr.height]="contentSize">
          <div xmlns="http://www.w3.org/1999/xhtml" class="center-content">
            <ng-content></ng-content>
          </div>
        </foreignObject>

        <!-- Start Icon (at 12 o'clock position) -->
        <g *ngIf="showStartIcon">
          <foreignObject
            [attr.x]="startIconPosition.x"
            [attr.y]="startIconPosition.y"
            [attr.width]="iconSize"
            [attr.height]="iconSize">
            <div xmlns="http://www.w3.org/1999/xhtml" class="icon-wrapper">
              <img [src]="startIconUrl" alt="Start icon" class="icon">
            </div>
          </foreignObject>
        </g>

        <!-- End Icon (at progress end position) -->
        <g *ngIf="showEndIcon">
          <foreignObject
            [attr.x]="endIconPosition.x"
            [attr.y]="endIconPosition.y"
            [attr.width]="iconSize"
            [attr.height]="iconSize">
            <div xmlns="http://www.w3.org/1999/xhtml" class="icon-wrapper">
              <img [src]="endIconUrl" alt="End icon" class="icon">
            </div>
          </foreignObject>
        </g>
      </svg>
    </div>
  `,
  styles: [`
    .progress-container {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .center-content {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .icon-wrapper {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: visible;
    }
  `]
})
export class CircularProgressComponent implements OnInit {
  @Input() progress: number = 0;
  @Input() color: string = '#4CAF50';
  @Input() bgColor: string = '#E6F7E9';
  @Input() secondaryColor: string = '#FFA500'; // Default orange color for progress > 100%
  @Input() radius: number = 80;
  @Input() strokeWidth: number = 20;
  @Input() size: number = 200;

  @Input() showStartIcon: boolean = false;
  @Input() startIconUrl: string = '';

  @Input() showEndIcon: boolean = false;
  @Input() endIconUrl: string = '';

  @Input() iconSize: number = 24;

  circumference: number = 0;
  primaryDashOffset: number = 0;
  secondaryDashOffset: number = 0;
  contentSize: number = 0;
  startIconPosition = { x: 0, y: 0 };
  endIconPosition = { x: 0, y: 0 };

  ngOnInit() {
    this.circumference = 2 * Math.PI * this.radius;
    this.calculateDashOffset();
    this.contentSize = this.size - (this.strokeWidth * 2);
    this.calculateIconPositions();
  }

  ngOnChanges() {
    this.circumference = 2 * Math.PI * this.radius;
    this.calculateDashOffset();
    this.contentSize = this.size - (this.strokeWidth * 2);
    this.calculateIconPositions();
  }

  calculateDashOffset() {
    // For the primary progress circle (capped at 100%)
    const primaryProgress = Math.min(this.progress, 100);
    const primaryProgressPercentage = primaryProgress / 100;
    this.primaryDashOffset = this.circumference * (1 - primaryProgressPercentage);

    // For the secondary progress circle (only the portion > 100%)
    if (this.progress > 100) {
      const secondaryProgress = this.progress - 100;
      const secondaryProgressPercentage = Math.min(secondaryProgress / 100, 1); // Cap at 100% for secondary arc
      this.secondaryDashOffset = this.circumference * (1 - secondaryProgressPercentage);
    }
  }

  calculateIconPositions() {
    // Start icon position (12 o'clock) - positioned directly on the circle path
    const startAngle = -90 * (Math.PI / 180); // -90 degrees = 12 o'clock position
    const startX = this.size / 2 + this.radius * Math.cos(startAngle);
    const startY = this.size / 2 + this.radius * Math.sin(startAngle);

    this.startIconPosition = {
      x: startX - (this.iconSize / 2),
      y: startY - (this.iconSize / 2)
    };

    // End icon position (based on progress)
    // Calculate the angle based on the actual progress (can exceed 100%)
    const endAngle = ((this.progress / 100) * 360 - 90) * (Math.PI / 180);

    // Position on the circle's path exactly where the progress ends
    const endX = this.size / 2 + this.radius * Math.cos(endAngle);
    const endY = this.size / 2 + this.radius * Math.sin(endAngle);

    this.endIconPosition = {
      x: endX - (this.iconSize / 2),
      y: endY - (this.iconSize / 2)
    };
  }
}
